import type { CSSProperties, HTMLAttributes, ReactNode } from "react";
import { cn } from "../lib/cn";

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  /** Explicit width (number → px). */
  width?: number | string;
  /** Explicit height (number → px). */
  height?: number | string;
  /** Fully-rounded pill (for text lines / avatars). */
  rounded?: boolean;
}

function toDim(v: number | string | undefined): string | undefined {
  if (v === undefined) return undefined;
  return typeof v === "number" ? `${v}px` : v;
}

/**
 * Loading placeholder — a muted, pulsing block. Respects `prefers-reduced-motion`
 * via Tailwind's `motion-reduce` variant (no animation when the user asks for
 * less). Size via `width`/`height` props or utility classes.
 */
export function Skeleton({
  width,
  height,
  rounded = false,
  className,
  style,
  ...rest
}: SkeletonProps): ReactNode {
  const dims: CSSProperties = {};
  const w = toDim(width);
  const h = toDim(height);
  if (w !== undefined) dims.width = w;
  if (h !== undefined) dims.height = h;

  return (
    <div
      aria-hidden="true"
      className={cn(
        "animate-pulse bg-muted/20 motion-reduce:animate-none",
        rounded ? "rounded-full" : "rounded-md",
        className,
      )}
      style={{ ...dims, ...style }}
      {...rest}
    />
  );
}

/** A common composite: a titled chart-card skeleton at a given height. */
export function ChartSkeleton({ height = 260 }: { height?: number }): ReactNode {
  return (
    <div className="space-y-3">
      <Skeleton width={140} height={14} rounded />
      <Skeleton height={height} />
    </div>
  );
}
